/**
 * Export/import-to-file. The owner OWNS their save and can back it up: export
 * serializes the live world to a JSON string (wrapped in a Blob/file by the UI);
 * import parses, migrates, and validates an arbitrary string back into a Save.
 *
 * Import is the one place a bad string SHOULD surface an error (the user picked a
 * file; tell them it's not a valid save) — unlike a silent DB read, which recovers
 * to a fresh game.
 */
import type { Save } from '@/shared';
import { migrate } from './migrations.js';
import { isSaveLike } from './validate.js';

/** Serialize a save to a pretty JSON string for export-to-file. */
export function exportSave(save: Save): string {
  return JSON.stringify(save, null, 2);
}

/**
 * Parse + migrate + validate an imported save string. Throws a clear error on
 * malformed JSON or a value that isn't a save (even after migration), so the UI
 * can show a friendly "that file isn't a Parallel Routes save" message.
 */
export function importSave(json: string): Save {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error('importSave: file is not valid JSON');
  }

  let migrated: Save;
  try {
    migrated = migrate(parsed);
  } catch (err) {
    throw new Error(
      `importSave: could not upgrade save (${err instanceof Error ? err.message : 'unknown'})`,
      { cause: err },
    );
  }

  if (!isSaveLike(migrated)) {
    throw new Error('importSave: file is not a valid Parallel Routes save');
  }
  return migrated;
}

/** Convenience: a Blob suitable for triggering a file download in the browser. */
export function saveToBlob(save: Save): Blob {
  return new Blob([exportSave(save)], { type: 'application/json' });
}

/** Convenience: read a File/Blob's text and import it. */
export async function importFromBlob(blob: Blob): Promise<Save> {
  return importSave(await blob.text());
}
